import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

import { baseUrl } from "../../../constants";
import { apiClient } from "../../api";
import {
  GET_USER_API_RESPONSE,
  GET_USERS_API_RESPONSE,
  InviteUserPayload,
  ValidatePinPayload,
  CreatePinPayload,
  ChangePinPayload,
  VALIDATE_PIN_API_RESPONSE,
  CREATE_PIN_API_RESPONSE,
  CHANGE_PIN_API_RESPONSE,
  User,
} from "./types";
import keys from "./keys";
import { baseURL } from "../../../utils";

/** Users of the signed-in business; the server reads businessId from the JWT. */
const usersResourcePath = "business-users/me";
const createPinResourcePath = "users/pin/create";
const validatePinResourcePath = "users/pin/validate";
const changePinResourcePath = "users/pin/change";

/** @param url full URL, usually `buildApiUrl(usersResourcePath)` */
const useGetUsers = (url: string) => {
  const {
    isLoading,
    data,
    isSuccess,
    error,
  }: UseQueryResult<GET_USERS_API_RESPONSE, unknown> = useQuery(
    [keys.read, url],
    () => apiClient.get({ url }),
  );
  return { isLoading, data, isSuccess, error };
};

const useGetUser = (id: string | undefined) => {
  const hash = [keys.read, `${id}`];
  const {
    isLoading,
    data,
    isSuccess,
    error,
  }: UseQueryResult<GET_USER_API_RESPONSE, unknown> = useQuery(
    hash,
    () => apiClient.get({ url: `${baseUrl}/business-users/${id}` }),
    {
      enabled: !!id,
    },
  );
  return { isLoading, data, isSuccess, error };
};

const getUser = async (id: string): Promise<User> => {
  const { data: user } = await apiClient.get({
    url: `${baseUrl}/business-users/${id}`,
  });
  return user;
};

const useInviteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // The server attaches the invite to the caller's business (from the JWT).
    mutationFn: async (data: InviteUserPayload) => {
      const response = await apiClient.post({
        url: `${baseUrl}/business-users/invite`,
        body: data,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.read],
      });
    },
  });
};

const useCreatePin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: CreatePinPayload,
    ): Promise<CREATE_PIN_API_RESPONSE> => {
      const response = await apiClient.post({
        url: `${baseURL}/${createPinResourcePath}`,
        body: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.read],
      });
    },
  });
};

const useValidatePin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: ValidatePinPayload,
    ): Promise<VALIDATE_PIN_API_RESPONSE> => {
      const response = await apiClient.post({
        url: `${baseURL}/${validatePinResourcePath}`,
        body: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.read] });
    },
  });
};

const useChangePin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: ChangePinPayload,
    ): Promise<CHANGE_PIN_API_RESPONSE> => {
      const response = await apiClient.post({
        url: `${baseURL}/${changePinResourcePath}`,
        body: data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.read] });
    },
  });
};

export {
  usersResourcePath,
  useGetUsers,
  useGetUser,
  getUser,
  useInviteUser,
  useValidatePin,
  useChangePin,
  useCreatePin,
};
